import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import { PrimaryButton } from '../../../../../../components/Button/ActionButtons';
// Reuse the single-exercise editor's video display so the preview looks consistent.
import { VideoContainer, LocalExerciseVideo, RemoteExerciseVideo } from '../../ExerciseEditor/VideoHandler/styles';
import { UploadStatus } from '../constants';
import { formatEta } from '../utils';
import texts from '../texts';
import { CheckIcon, FilmIcon, WarnIcon } from '../icons';
import {
  VideoBox,
  VideoOverlay,
  Ring,
  OverlayPct,
  VideoFail,
  VideoFailText,
  ProgressBar,
  VideoStat,
  VideoError,
  VideoControls,
} from '../styles';

/**
 * The left column of an exercise card: the video preview/upload box and its
 * controls, rendered per upload state. The video preview reuses the editor's
 * VideoContainer + LocalExerciseVideo (no delete icon) so it matches the
 * single-exercise editor. Presentational only — actions delegate to the parent.
 */
const VideoColumn = ({
  cardId,
  upload,
  previewUrl,
  remoteVideoUrl,
  fileName,
  onRetryUpload,
  onRemoveVideo,
  onAddVideo,
}) => {
  if (!upload || upload.status === UploadStatus.NONE) {
    return (
      <>
        <VideoBox>
          <VideoFail $muted>
            <FilmIcon />
            <VideoFailText>{texts.noVideo}</VideoFailText>
          </VideoFail>
        </VideoBox>
        <VideoStat>{texts.noVideoNote}</VideoStat>
        <VideoControls>
          <PrimaryButton size="small" variant="light" onClick={() => onAddVideo(cardId)}>
            {texts.addVideo}
          </PrimaryButton>
        </VideoControls>
      </>
    );
  }

  if (upload.status === UploadStatus.QUEUED) {
    // Waiting for an upload slot — starts automatically as slots free up.
    return (
      <>
        <VideoContainer>
          {previewUrl && <LocalExerciseVideo src={previewUrl} controls />}
        </VideoContainer>
        <VideoStat>
          <FilmIcon />
          {fileName}
        </VideoStat>
        <VideoStat>{texts.queuedNote}</VideoStat>
      </>
    );
  }

  if (upload.status === UploadStatus.UPLOADING) {
    const progress = upload.progress || 0;
    // ETA appears once enough bytes have moved to estimate the upload speed; a
    // move-only retry (bar already full) never shows one.
    const showEta = upload.etaSeconds != null && progress > 0 && progress < 100;
    return (
      <>
        <VideoContainer>
          {previewUrl && <LocalExerciseVideo src={previewUrl} />}
          <VideoOverlay>
            <Ring />
            <OverlayPct>{format(texts.uploadingBadge, { progress })}</OverlayPct>
          </VideoOverlay>
        </VideoContainer>
        <ProgressBar $progress={progress}>
          <i />
        </ProgressBar>
        {showEta && (
          <VideoStat>{format(texts.uploadTimeLeft, { time: formatEta(upload.etaSeconds) })}</VideoStat>
        )}
        <VideoStat>
          <FilmIcon />
          {fileName}
        </VideoStat>
      </>
    );
  }

  if (upload.status === UploadStatus.FAILED) {
    return (
      <>
        <VideoBox $failed>
          <VideoFail>
            <WarnIcon />
            <VideoFailText>{texts.uploadFailedBadge}</VideoFailText>
          </VideoFail>
        </VideoBox>
        {fileName && (
          <VideoStat>
            <FilmIcon />
            {fileName}
          </VideoStat>
        )}
        <VideoError>{upload.error || texts.uploadFailedGeneric}</VideoError>
        <VideoControls>
          {upload.failedStep ? (
            // A real upload-step failure — retry resumes from the failed step
            // (a completed byte upload is never repeated).
            <PrimaryButton size="small" variant="warning" onClick={() => onRetryUpload(cardId)}>
              {texts.retryUpload}
            </PrimaryButton>
          ) : (
            // Nothing to retry: either a local validation failure (e.g.
            // oversize) or a restored card whose file died with the page.
            <PrimaryButton size="small" variant="light" onClick={() => onAddVideo(cardId)}>
              {upload.fileLost ? texts.chooseFile : texts.chooseDifferentFile}
            </PrimaryButton>
          )}
          <PrimaryButton size="small" variant="danger" onClick={() => onRemoveVideo(cardId)}>
            {texts.removeVideo}
          </PrimaryButton>
        </VideoControls>
      </>
    );
  }

  // uploaded — a restored card has no local preview (the File died with the
  // page), so fall back to the created exercise's embeddable Vimeo player.
  return (
    <>
      <VideoContainer>
        {previewUrl && <LocalExerciseVideo src={previewUrl} controls />}
        {!previewUrl && remoteVideoUrl && <RemoteExerciseVideo src={remoteVideoUrl} />}
      </VideoContainer>
      <VideoStat $ok>
        <CheckIcon />
        {fileName}
      </VideoStat>
    </>
  );
};

VideoColumn.propTypes = {
  cardId: PropTypes.string.isRequired,
  upload: PropTypes.shape({
    status: PropTypes.string,
    progress: PropTypes.number,
    etaSeconds: PropTypes.number,
    error: PropTypes.string,
    failedStep: PropTypes.number,
    fileLost: PropTypes.bool,
  }),
  previewUrl: PropTypes.string,
  remoteVideoUrl: PropTypes.string,
  fileName: PropTypes.string,
  onRetryUpload: PropTypes.func.isRequired,
  onRemoveVideo: PropTypes.func.isRequired,
  onAddVideo: PropTypes.func.isRequired,
};

VideoColumn.defaultProps = {
  upload: null,
  previewUrl: null,
  remoteVideoUrl: null,
  fileName: '',
};

export default VideoColumn;
